import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DeviceType, FormCreationData } from "../../../../../stateManager/store/formCreation_v2/types";
import { useTopElement } from "../hooks/useTopElement";
import { useDispatch, useSelector } from "react-redux";
import { onSetBorderVisibility, onsetClrPicContext, onsetDropShadowVisibility, onSetErrorFontStylesVisibility, onSetFontStylesVisibility, onSetTextEditorVisibility } from "../../../../../stateManager/store/common/action";
import { getMargin } from "../helpers/generateStyles";
import { setMultipleSelectionv2 } from "../../../../../stateManager/store/contextMenuv2/action";
import { addComputedStyles, addWrapperComputedStyles, pushFormActionStack, removeElement, reorderElementReference, reorderUndoElementReference, setModalVisibility, setSelectedFormItemId, setSnackbarVisibility, undoDeleteElement, updateElement, updatedSelectedeElements } from "../../../../../stateManager/store/formCreation_v2/actions";
import { clearSelectedItems } from "../../../../../stateManager/store/contextMenu/action";
import { myRedoStack, myUndoStack } from "../stack/Stack";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "../types/ItemTypes";
import { getDimensions } from "../helpers/getDimensions";
import { flexContainers } from "../helpers/handleContainers";
import { conditionalElements } from "../constants";
import { getElementStyleByType } from "../helpers/formsv3/getComputedValues";
import { includes } from "lodash";
import { getComponents } from "../helpers";
import { MouseHoverOnTree } from "../../../../../helpers/RxServices";

declare const window: any;

type Props = {
  deviceType: 'Sm' | 'Md' | 'Lg';
  element: FormCreationData;
  onDelete?: (e: React.MouseEvent<SVGElement, MouseEvent>) => void
  onCopy?: (e: React.MouseEvent<SVGElement, MouseEvent>) => void
  zoomScale?: any
  parentElement?: any
  index?: any
  isCheckedIn?: any
  isRootChildren?: any
};

function getFontSize(deviceType: DeviceType): string {
  switch (deviceType) {
    case "Lg":
      return "text-xl";
    case "Md":
      return "text-base";
    default:
      return "text-xs";
  }
}

function getTopPosition(deviceType: DeviceType): string {
  switch (deviceType) {
    case "Lg":
      return "-top-7";
    case "Md":
      return "-top-6";
    default:
      return "-top-4";
  }
}

function MicroComponents(props: Props) {
  const { selectedElementId, isCoreV3, modal } = useSelector((state: any) => state.formJson);
  const { visible } = modal;
  const { element, deviceType, zoomScale = "", onCopy, onDelete } = props
  const { index, isCheckedIn } = props;
  const dispatch = useDispatch()
  const [isTop, setIsTop] = useTopElement()
  const { colorPickerContext, dropShadowVisibility, borderVisibility, textEditorVisibility, fontStylesVisibility, errorFontStylesVisibility } = useSelector((state: any) => state.common);
  const { id, type, widgetName, properties, isAsset, isChildAsset, parentId, label } = element;
  const [isAppendedToFixedRoot, setIsAppendedToFixedRoot] = useState(false)
  const portalRef: any = useRef(null)
  const styles = element?.properties?.styles?.[deviceType] || {}
  const { components } = getComponents();
  const component: FormCreationData = components[selectedElementId] || {};
  const newStyles = {
    ...component?.properties?.styles?.common,
    ...component?.properties?.styles?.[deviceType],
  }
  const divRef = useRef<HTMLDivElement>(null);
  const [hoveredElId, setHoveredElId] = useState<any>()

  const closeModals = () => {
    if (colorPickerContext?.visibility) {
      dispatch(onsetClrPicContext({ ...colorPickerContext, visibility: false }))
    }
    if (dropShadowVisibility) {
      dispatch(onsetDropShadowVisibility(false))
    }
    if (borderVisibility) {
      dispatch(onSetBorderVisibility(false))
    }
    if (fontStylesVisibility) {
      dispatch(onSetFontStylesVisibility(false))
    }
    if (errorFontStylesVisibility) {
      dispatch(onSetErrorFontStylesVisibility(false))
    }
    if (textEditorVisibility) {
      dispatch(onSetTextEditorVisibility(false))
    }
  }

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.ELEMENT, json: { ...element, index } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isCheckedIn && !isChildAsset,
  });

  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [
      ItemTypes.ELEMENT,
    ],
    canDrop: (item: any) => {
      // 1. to prevent the source element from dropping into itself
      if (id === item.json.id) {
        return false
      }

      // 2. only sibling elements can be dropped
      return element.parentId === item.json.parentId;
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      };
    },
    drop: (item: any) => {
      if (isOverCurrent) {
        const { json } = item
        const dropTarget = {
          index,
          id,
          parentId
        }
        const dragSource = {
          id: json.id,
          index: json.index,
          parentId: json.parentId,
        }
        // undoredo for drag and drop
        const payload: any = { dropTarget, dragSource }
        myUndoStack.push({ ...payload });
        dispatch(pushFormActionStack({
          undo: () => {
            const poppedElement = myUndoStack.pop();
            if (poppedElement) {
              myRedoStack.push(poppedElement);
              dispatch(reorderUndoElementReference({
                dropTarget: {
                  index: poppedElement.dragSource.index,
                  id: "",
                  parentId: poppedElement.dragSource.parentId,
                },
                dragSource: {
                  ...poppedElement.dragSource,
                  index: poppedElement.dropTarget.index,
                  parentId: poppedElement.dropTarget.parentId,
                },
                increment: -1
              }))
            }
          },
          redo: () => {
            const poppedElement = myRedoStack.pop();
            if (poppedElement) {
              myUndoStack.push({ ...poppedElement });
              dispatch(reorderUndoElementReference({
                dropTarget: poppedElement.dropTarget,
                dragSource: poppedElement.dragSource,
                increment: 1
              }))
            }
          }
        }));

        dispatch(reorderElementReference({
          dropTarget,
          dragSource,
        }))
      }
    },
  });


  // Handle select element
  const handleSelect = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    closeModals();
    if (type !== "SnackBar") {
      dispatch(setSnackbarVisibility({ visible: false, componentId: "" }));
    }
    e.stopPropagation();
    if (e.shiftKey && !isChildAsset) {
      dispatch(setMultipleSelectionv2(true));
      dispatch(updatedSelectedeElements(id));
    } else {
      const existingWrapper = document.getElementById("wrapper");
      existingWrapper && await existingWrapper.remove();
      await dispatch(setMultipleSelectionv2(false));
      await dispatch(
        setSelectedFormItemId({
          id,
          isAssetInstanceSelected: isAsset || isChildAsset,
        })
      );
      await dispatch(clearSelectedItems())
    }
  };

  const handleRightClick = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    const existingWrapper = document.getElementById("wrapper");
    existingWrapper && await existingWrapper.remove();
    await dispatch(
      setSelectedFormItemId({
        id,
        isAssetInstanceSelected: isAsset || isChildAsset,
      })
    );
    await dispatch(clearSelectedItems());
  };

  const getHeight = (): string => {
    let returnVal = "";
    if (style[deviceType]?.heightMode === 'auto') {
      returnVal = "auto"
    } else if (style[deviceType]?.heightMode === 'min-content') {
      returnVal = "min-content"
    } else if (style[deviceType]?.heightMode === 'max-content') {
      returnVal = "max-content"
    } else if (style[deviceType]?.heightMode === '%' || style[deviceType]?.heightMode === 'px' && style[deviceType]?.height) {
      returnVal = `${style[deviceType]?.height}${style[deviceType]?.heightMode}`
    } else {
      returnVal = "fit-content";
    }
    return returnVal
  }
  const getWidth = (): string => {
    const calculatedWidth = getDimensions(style[deviceType]?.width, style[deviceType]?.widthMode)
    if (calculatedWidth) {
      return calculatedWidth;
    }
    return ""
  }
  
  const handleLabelChangeOnDoubleClick = (componentId: string, text: string) => {
    dispatch(updateElement({
      id: componentId,
      path: (type === "Text" || type === "Uploader" || type === "Link" || type === "Scanner" || type === "Selfie") ? "properties.text" : (type === "Select" || type === "Otp") ? "properties.config.labelName" : type === "Stats" ? "properties.config.title" : type === "SignaturePad" ? "properties.config.placeholder" : "label",
      value: text
    }))
  }

  const handleTitleChangeOnDoubleClick = (componentId: string, text: string) => {
    dispatch(updateElement({
      id: componentId,
      path: "properties.config.title",
      value: text
    }))
  }

  const handleChangeSegmentLabel = (segmentsData: []) => {
    dispatch(updateElement({
      id: id,
      path: `properties.config.segments`,
      value: segmentsData
    }))
  }

  const handleChangeTagLabel = (tagOptions: []) => {
    dispatch(updateElement({
      id: id,
      path: `properties.data.options`,
      value: tagOptions
    }))
  }

  const style = element?.properties?.styles || {};
  let flexGrow = 0
  let flexShrink = 0;
  if (style && style[deviceType]) {
    flexGrow = parseInt(style[deviceType].flexGrow || "0")
    flexShrink = parseInt(style[deviceType].flexShrink || "0")
  }
  const dimensionStyle: React.CSSProperties = {
    height: getHeight(),
    width: getWidth(),
    top: `${style[deviceType]?.top ? `${style[deviceType]?.top}px` : ""}`,
    left: `${style[deviceType]?.left ? `${style[deviceType]?.left}px` : ""}`,
    bottom: `${style[deviceType]?.bottom ? `${style[deviceType]?.bottom}px` : ""}`,
    right: `${style[deviceType]?.right ? `${style[deviceType]?.right}px` : ""}`,
    overflowY: flexContainers.includes(type) ? 'auto' : 'hidden',
    zIndex: style[deviceType]?.zIndex ? style[deviceType]?.zIndex : 'unset'
  }

  const isParentFlex = (props.parentElement && props.parentElement.type === 'Column') || (props.parentElement && props.parentElement.type === 'Row')

  const gridSpanClasses = `col-span-${style[deviceType]?.span || (deviceType === "Sm" ? 1 : deviceType === "Md" ? 6 : 12)} row-span-${style[deviceType]?.rowSpan}`;
  const xAlignGen = !isParentFlex ? `justify-self-${style[deviceType]?.alignX}` : '';
  const yAlignGen = !isParentFlex ? `self-${style[deviceType]?.alignY}` : '';
  const visibilityGen = `${style[deviceType]?.visible === true ? "block" : style[deviceType]?.visible ? "block" : "hidden"
    }`;
  const positionGen = `${style[deviceType]?.absolute ? "absolute" : "relative"
    }`;

  const isActive = isOverCurrent && canDrop;
  const isElementFixed = style[deviceType]?.fixed;
  const isElementFixedRef = useRef(isElementFixed)
  const elementStyle = properties?.styles || {};
  const marginStyle = getMargin(elementStyle[deviceType])

  useEffect(() => {
    const payload = {
      ...element,
      deviceType,
      visibility: true,
      zoomScale,
      onCopy,
      onDelete,
      onDoubleClick: handleLabelChangeOnDoubleClick,
      renderer: false,
      onEditingSegment: handleChangeSegmentLabel,
      onEditingTag: handleChangeTagLabel,
      onDoubleClickTitle: handleTitleChangeOnDoubleClick,
    }
    if (window[type]) {
      const { type: pluginType, data: plugin } = window[type];
      if (pluginType === "Package") {
        plugin.widgets[widgetName].render(id, payload);
      } else if (pluginType === "Widget") {
        plugin.render(id, payload)
      }
    }
  }, [id, type, widgetName, deviceType, element, zoomScale, isAppendedToFixedRoot])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsTop(id)
  }, [id, setIsTop])

  const elementRef = useRef<HTMLElement | null>(null!);

  const memoizedAddWrapperComputedStyles = useCallback((styles) => {
    dispatch(addWrapperComputedStyles(styles));
  }, [dispatch, styles]);
  
  const memoizedAddComputedStyles = useCallback((styles) => {
    dispatch(addComputedStyles(styles));
  }, [dispatch, styles]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const getCurrentStyles = () => {
      const element = document.getElementById(`${type}-${id}`);
      if (element) {
        elementRef.current = element;
        const styles = getElementStyleByType(type, id);
        return styles;
      }
      return null;
    };
  
    if (type === "A8Modal" || type === "Row" || type === "Column" || type === "Grid") {
      if (timer) {
        clearTimeout(timer)
      }
      timer = setTimeout(() => {
        const styles = getCurrentStyles();
        if (styles) {
          dispatch(addWrapperComputedStyles(styles));
        }
      }, 0)
    } else if (selectedElementId === id) {
      dispatch(addWrapperComputedStyles({}));
      timer = setTimeout(() => {
        const styles = getCurrentStyles();
        if (styles) {
          dispatch(addComputedStyles(styles));
        }
      },30);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [selectedElementId, id, deviceType, label, type, memoizedAddWrapperComputedStyles, memoizedAddComputedStyles]);

  useEffect(() => {
    const grid = document.getElementById("mainGrid");
    if (visible && grid) {
      grid.style.overflow = "hidden";
    } else if (grid) {
      grid.style.overflow = "auto";
    }
  }, [visible])

  // use to apply latest styles
  const fixedelemClass = useMemo(() => {
    const heigthClass = 'h-full';
    const widthClass = newStyles?.width == null ? `${type}-wrapper` : 'w-full';
    return `${heigthClass} ${widthClass}`;
  }, [newStyles?.width, type]);

  useLayoutEffect(() => {
    const fixedRoot = document.getElementById("canvasFixedElement");
    if (fixedRoot && isElementFixed && styles?.fixed) {
        fixedRoot.style.margin = marginStyle.margin;
        fixedRoot.style.marginTop = marginStyle.marginTop;
        fixedRoot.style.marginRight = marginStyle.marginRight;
        fixedRoot.style.marginBottom = marginStyle.marginBottom;
        fixedRoot.style.marginLeft = marginStyle.marginLeft;
    } else {
      return
    }
    if (divRef.current && divRef.current.className !== fixedelemClass) {
      divRef.current.className = fixedelemClass;
    }
  }, [
    marginStyle, 
    fixedelemClass, 
    isElementFixed, 
    isCoreV3, 
    styles, 
  ]);
  useEffect(() => {
    const subscription = MouseHoverOnTree.getMessage().subscribe(function (e) {
      setHoveredElId(e);
    });
    if (!subscription) {
      MouseHoverOnTree.getMessage();
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [hoveredElId]);

  if (isElementFixed && isCoreV3) {
    const fixedRoot = document.getElementById("canvasFixedElement")
    if (fixedRoot) {
      const elementNode = document.getElementById(element.id)
      if (elementNode?.children.length) {
        return portalRef.current
      }
      const portal = createPortal(<div
        id={id}
        ref={divRef}
        style={{ height: "100%", width: "100%" }}  
        className={`${fixedelemClass}`}
        onClick={(e) => handleSelect(e, id)} 
        onContextMenu={(e) => {
          e.preventDefault();
          handleRightClick(e,id);
        }}>
        {((): any => {
          if (!isAppendedToFixedRoot) {
            setIsAppendedToFixedRoot(true)
          }
        })()}
        created via portal
      </div>, fixedRoot)

      portalRef.current = portal

      return portal
    }

    return null
  }

  const calculateDisplay = (type?: string) => {
    const isVisible = style[deviceType]?.visible === true;

    if (!isVisible) {
      return "none";
    } else if (
      type &&
      (type === "Button" || type === "Input" || type === "Select" || type === "Textarea" ||
        type === "Text" || type === "ImageViewer" || type === "ImageSlider")
    ) {
      return "inline-block";
    } else {
      return "block";
    }
  };

  const setheightforsnack = () => {
    if (type !== "SnackBar" && dimensionStyle.height !== "%") {
      return dimensionStyle.height ? { height: dimensionStyle.height } : { height: "40px" };
    } else {
      return {}; 
    }
  };
  
  const coreV3Styles: React.CSSProperties = {
    display: calculateDisplay(type),
    flexGrow,
    flexShrink,
    ...(dimensionStyle.left && positionGen === 'absolute' ? { left: dimensionStyle.left } : { left: "" }),
    ...(dimensionStyle.right && positionGen === 'absolute' ? { right: dimensionStyle.right } : { right: "" }),
    ...(dimensionStyle.top && positionGen === 'absolute' ? { top: dimensionStyle.top } : { top: "" }),
    ...(dimensionStyle.bottom && positionGen === 'absolute' ? { bottom: dimensionStyle.bottom } : { bottom: "" }),
    ...(dimensionStyle.width ? { width: dimensionStyle.width } : {}),
    ...setheightforsnack(),
    ...(dimensionStyle.zIndex ? { zIndex: dimensionStyle.zIndex } : {}),
    ...marginStyle
  }

  if (!visible && isCoreV3 && conditionalElements.includes(type)) {
    return null
  }

  if (isCoreV3) {
    return (
      <>
        {type === "Spacer" || type === "Space" ? (
          <div
            ref={(ref) => drag(drop(ref))}
            id={id}
            key={id}
            className={`${type}-wrapper ${positionGen} ${gridSpanClasses}
             ${xAlignGen}
             ${yAlignGen} ${isActive ? "bg-red-400" : ""}`}
            style={coreV3Styles}
            onClick={(e) => {
              handleSelect(e, id);
              closeModals();
            }}
            onContextMenu={(e) => {
              e.preventDefault(); // Prevent the default context menu
              handleRightClick(e,id);
            }}
          >
            <div
              id={`${type}-${id}`}
              style={{
                minWidth: '100%',
                height: "100%",
                minHeight: getHeight(),
              }}
            ></div>
          </div>
        ) : (
          <div
            ref={(ref) => drag(drop(ref))}
            id={id}
            key={id}
            data-test-id={`${type}-wrapper-${id}`}
            className={`${type}-wrapper ${positionGen} ${gridSpanClasses}
            ${xAlignGen}
            ${yAlignGen} ${isActive ? "bg-red-400" : ""}`}
            style={{ ...coreV3Styles, backgroundColor: hoveredElId === id ? "#EFF0F4" : "" }}
            onMouseOver={(e) => {
              e.stopPropagation();
              MouseHoverOnTree.sendMessage(id)
            }}
            onMouseLeave={() => { MouseHoverOnTree.sendMessage(null) }}
            onClick={(e) => {
              handleSelect(e, id);
              closeModals();
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              handleRightClick(e,id); 
            }}
          ></div>
        )}
      </>
    );
  }
  const excludedTypes = ["FAB", "SnackBar", "A8Modal", "CustomModal"];

  return (
    <>
      {!excludedTypes.includes(type) && selectedElementId === id && <p
        data-test-id={id}
        id={`tag_${id}`}>
      </p>
      }
      <div id={id} style={{ width: '100%', position: "relative" }} onClick={() => closeModals()}>
      </div>
    </>
  );
}

export default MicroComponents;